import { styled } from "@mui/material";
import { CSSProperties, ReactNode, useEffect, useId, useRef } from "react";

/**
 * ReelNumber — slot-machine digit reels (transitions.dev "Spinning counter", #26).
 *
 * Every digit in `value` becomes a clipped window (`.t-reel-col`) over a vertical
 * strip (`.t-reel-strip`) of 0-9 cells repeated `SPINS + 1` times. When a digit
 * changes (on first mount it comes from 0) the strip is snapped to the previous
 * digit in the *last* block with no transition, reflowed, then tweened down to
 * the target in the first block — so it spins through `SPINS` full rotations,
 * digits dropping in from above, and lands exactly where it rests (no snap on
 * settle). Columns stagger left → right via `--reel-stagger`. Non-digit characters (`,` `.` `$` ` ` and
 * unit suffixes) render as static cells so the string keeps its shape.
 *
 * Motion is compositor-only (`translate3d`). The vertical streak is a directional
 * SVG `feGaussianBlur stdDeviation="0 Y"` (CSS `blur()` would smear sideways),
 * SMIL-decayed to 0 over each column's own window. The filter, the edge mask and
 * `will-change` are only set for the duration of a spin and cleared on settle.
 *
 * Tunables (`--reel-dur`, `--reel-cell`, `--reel-spin-blur`, `--reel-stagger`,
 * `--reel-ease`) are installed on `:root` in App.tsx and can be overridden per
 * instance through `style` / `sx`. `--reel-cell` must match the surrounding line
 * box (default `1.3em`) or the reel will nudge its neighbours; it is rounded to
 * whole px where `round()` is supported (see the `@supports` block).
 */

/** Full 0-9 rotations each reel makes before it lands. */
const SPINS = 2;
const STRIP = Array.from({ length: (SPINS + 1) * 10 }, (_, i) => i % 10);

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Parse a CSS <time> custom property ("1400ms" | "1.4s") into milliseconds. */
const parseMs = (raw: string, fallback: number) => {
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) {
    return fallback;
  }
  return raw.trim().endsWith("ms") ? n : n * 1000;
};

// Offset as a fraction of the strip's own height rather than `n * var(--reel-cell)`:
// layout snaps each cell to 1/64px, so the calc() drifted ~0.0125px per cell and
// columns resting on different digits sat at different heights (a 9 ~0.11px above
// a 0). The strip's height is the sum of those snapped cells, so a percentage
// lands on the exact layout position of cell n in every column.
const cellOffset = (cells: number) => `translate3d(0, ${(-cells / STRIP.length) * 100}%, 0)`;

const Reel = styled("span")`
  position: relative;
  display: inline-flex;
  align-items: center;
  vertical-align: top;
  height: var(--reel-cell);
  font-variant-numeric: tabular-nums;
  white-space: pre;

  .t-reel-col {
    position: relative;
    height: var(--reel-cell);
    overflow: hidden;
  }
  /* Soft-fade the window edges instead of hard-cropping. Applied only while the
     reel is moving: at rest the cell is only ~1.3em tall, so a permanent fade
     would eat into the glyphs' cap height. */
  .t-reel-col.is-spinning {
    -webkit-mask-image: linear-gradient(to bottom, transparent 0%, #000 22%, #000 78%, transparent 100%);
    mask-image: linear-gradient(to bottom, transparent 0%, #000 22%, #000 78%, transparent 100%);
  }
  .t-reel-strip {
    display: flex;
    flex-direction: column;
  }
  .t-reel-digit,
  .t-reel-static {
    height: var(--reel-cell);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Snap the cell to whole CSS px. At a fractional cell height a resting strip sits
     at a fractional translate, and Chrome rounds the transformed glyphs to a
     different device pixel than the static ones — digits landed 1–2px above or
     below their neighbours depending on which digit (and font size, and DPR).
     With an integer cell every offset is integer, so all glyphs round alike. */
  @supports (height: round(1.3em, 1px)) {
    &,
    .t-reel-col,
    .t-reel-digit,
    .t-reel-static {
      height: round(var(--reel-cell), 1px);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .t-reel-strip {
      transition: none !important;
      filter: none !important;
    }
  }
`;

const SrOnly = styled("span")`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
`;

type ReelColumnProps = {
  digit: number;
  /** Position among the digit columns, left → right; drives the stagger. */
  index: number;
};

const ReelColumn = ({ digit, index }: ReelColumnProps) => {
  const filterId = `t-reel-blur-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const colRef = useRef<HTMLSpanElement>(null);
  const stripRef = useRef<HTMLSpanElement>(null);
  const blurRef = useRef<SVGAnimateElement>(null);
  // Digit the strip is currently resting on (first block). A fresh strip sits at 0.
  const restingRef = useRef(0);
  // Only the digit change should trigger a spin — a re-index (the number grew a
  // column) must not re-spin every reel, so the stagger index lives in a ref.
  const indexRef = useRef(index);
  indexRef.current = index;

  useEffect(() => {
    const col = colRef.current;
    const strip = stripRef.current;
    if (!col || !strip) {
      return undefined;
    }

    const from = restingRef.current;
    restingRef.current = digit;

    if (prefersReducedMotion()) {
      strip.style.transition = "none";
      strip.style.transform = cellOffset(digit);
      return undefined;
    }

    let timer = 0;
    const settle = () => {
      window.clearTimeout(timer);
      strip.removeEventListener("transitionend", onEnd);
      strip.removeEventListener("transitioncancel", onEnd);
      // Pin the resting digit in the first block (where the spin already landed —
      // this only moves anything on a cancelled spin) and drop every spin-only cost.
      strip.style.transition = "none";
      strip.style.transform = cellOffset(digit);
      strip.style.willChange = "";
      strip.style.filter = "";
      col.classList.remove("is-spinning");
    };
    const onEnd = (e: TransitionEvent) => {
      if (e.propertyName === "transform") {
        settle();
      }
    };

    // Reset to where we're coming from in the last block with no transition, reflow
    // so the browser commits that position, then tween down to the target in the
    // first block.
    strip.style.transition = "none";
    strip.style.transform = cellOffset(SPINS * 10 + from);
    void strip.offsetHeight;

    const css = getComputedStyle(strip);
    const stagger = indexRef.current;
    const duration = parseMs(css.getPropertyValue("--reel-dur"), 1400);
    const delay = parseMs(css.getPropertyValue("--reel-stagger"), 90) * stagger;
    const blur = parseFloat(css.getPropertyValue("--reel-spin-blur")) || 0;

    col.classList.add("is-spinning");
    strip.style.willChange = "transform";
    if (blur > 0) {
      strip.style.filter = `url(#${filterId})`;
    }
    strip.style.transition = `transform var(--reel-dur) var(--reel-ease) calc(var(--reel-stagger) * ${stagger})`;
    strip.style.transform = cellOffset(digit);
    strip.addEventListener("transitionend", onEnd);
    strip.addEventListener("transitioncancel", onEnd);
    // Safety net: transitionend never fires if the reel is hidden mid-spin.
    timer = window.setTimeout(settle, delay + duration + 100);

    // Decay the vertical streak over this column's own window. The ease front-loads
    // the velocity, so hold full blur briefly and be crisp well before landing.
    const anim = blurRef.current;
    if (anim && blur > 0) {
      anim.setAttribute("values", `0 ${blur};0 ${blur};0 0;0 0`);
      anim.setAttribute("dur", `${duration}ms`);
      anim.beginElementAt(delay / 1000);
    }

    return settle;
  }, [digit, filterId]);

  return (
    <span className="t-reel-col" ref={colRef} aria-hidden="true">
      <span className="t-reel-strip" ref={stripRef}>
        {STRIP.map((d, i) => (
          <span key={i} className="t-reel-digit">
            {d}
          </span>
        ))}
      </span>
      <svg width="0" height="0" aria-hidden="true" focusable="false" style={{ position: "absolute" }}>
        <filter id={filterId}>
          <feGaussianBlur stdDeviation="0 0">
            <animate
              ref={blurRef}
              attributeName="stdDeviation"
              keyTimes="0;0.15;0.6;1"
              begin="indefinite"
              fill="freeze"
            />
          </feGaussianBlur>
        </filter>
      </svg>
    </span>
  );
};

export type ReelNumberProps = {
  /** Pre-formatted display string (e.g. `toReadable(...)`). Digits spin, everything else is static. */
  value: string | number;
  /** Static leading cell — e.g. a currency symbol that carries its own font-size. */
  prefix?: ReactNode;
  /** Static trailing cell — e.g. a unit. */
  suffix?: ReactNode;
  className?: string;
  style?: CSSProperties;
};

const ReelNumber = ({ value, prefix, suffix, className, style }: ReelNumberProps) => {
  const text = String(value);
  const chars = Array.from(text);
  let digitIndex = 0;

  return (
    <Reel className={["t-reel", className].filter(Boolean).join(" ")} style={style}>
      <SrOnly>
        {prefix}
        {text}
        {suffix}
      </SrOnly>
      {prefix != null && (
        <span className="t-reel-static" aria-hidden="true">
          {prefix}
        </span>
      )}
      {chars.map((ch, i) => {
        // Key from the right so the ones/tens/… columns keep their identity (and
        // their resting digit) when the number gains or loses a leading column.
        const place = chars.length - 1 - i;
        if (ch >= "0" && ch <= "9") {
          return <ReelColumn key={`d${place}`} digit={Number(ch)} index={digitIndex++} />;
        }
        return (
          <span key={`s${place}`} className="t-reel-static" aria-hidden="true">
            {ch}
          </span>
        );
      })}
      {suffix != null && (
        <span className="t-reel-static" aria-hidden="true">
          {suffix}
        </span>
      )}
    </Reel>
  );
};

export default ReelNumber;
